import React, { useEffect, useState } from 'react';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';
import ScrollToTop from '../Components/ScrollToTop';
import '../assets/css/video-showcase.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

function getVideoSrc(url) {
  if (!url) return '';
  if (typeof window !== 'undefined' && url.startsWith('/uploads/videos/')) {
    return window.location.origin + url;
  }
  return url;
}

function VideoShowcasePage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(buildUrl('/api/videos'))
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && Array.isArray(data.videos)) {
          setVideos(data.videos);
        }
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const [active, setActive] = useState(null);

  const openModal = (video) => {
    setActive(video);
  };

  const closeModal = () => {
    setActive(null);
  };

  const renderModalContent = () => {
    if (!active) return null;
    const src = getVideoSrc(active.url);
    const isUploaded = (active.url || '').startsWith('/uploads/videos/');
    const isYouTube = /youtube\.com|youtu\.be/i.test(active.url || '');

    if (isUploaded) {
      return (
        <video
          src={src}
          controls
          autoPlay
          preload="metadata"
          playsInline
          className="video-showcase-modal-player"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isYouTube) {
      let embedUrl = active.url;
      try {
        const u = new URL(active.url);
        if (u.hostname.includes('youtu.be')) {
          const id = u.pathname.replace('/', '');
          embedUrl = `https://www.youtube.com/embed/${id}`;
        } else if (u.searchParams.get('v')) {
          embedUrl = `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
        }
      } catch (_) {
        // ignore, fall back to original url
      }
      return (
        <iframe
          src={embedUrl}
          title={active.title || 'Video'}
          className="video-showcase-modal-player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    return (
      <video
        src={src}
        controls
        autoPlay
        preload="metadata"
        playsInline
        className="video-showcase-modal-player"
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <>
      <HeaderOne />
      <ScrollToTop />

      <section className="video-showcase-hero">
        <div className="container th-container">
          <div className="video-showcase-hero-inner">
            <span className="video-showcase-tagline">Curated journeys, captured on film</span>
            <h1 className="video-showcase-title">Video Showcase</h1>
            <p className="video-showcase-subtitle">
              Explore our collection of travel stories across Travel with dinesh, Ape pansala, Urumaya and Ranga Rata.
            </p>
          </div>
        </div>
      </section>

      <section className="video-showcase-grid-section">
        <div className="container th-container">
          {loading ? (
            <div className="video-showcase-loading">
              <p>Loading videos…</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="video-showcase-loading">
              <p>No videos have been added yet.</p>
            </div>
          ) : (
            <div className="video-showcase-grid">
              {videos.map((video) => {
                const src = getVideoSrc(video.url);
                const isUploaded = (video.url || '').startsWith('/uploads/videos/');
                return (
                  <button
                    type="button"
                    key={video._id}
                    className="video-showcase-card"
                    onClick={() => openModal(video)}
                  >
                    <div className="video-showcase-thumb-wrap">
                      {isUploaded ? (
                        <video
                          src={src}
                          preload="metadata"
                          muted
                          playsInline
                          className="video-showcase-thumb"
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <div className="video-showcase-thumb video-showcase-thumb--placeholder">
                          <span className="video-showcase-thumb-label">External video</span>
                        </div>
                      )}
                      <div className="video-showcase-play-overlay">
                        <span className="video-showcase-play-icon">▶</span>
                      </div>
                    </div>
                    <div className="video-showcase-card-body">
                      <h3 className="video-showcase-card-title">{video.title}</h3>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div
          className="video-showcase-modal-overlay"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div className="video-showcase-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="video-showcase-modal-close"
              onClick={closeModal}
              aria-label="Close video"
            >
              ×
            </button>
            {renderModalContent()}
          </div>
        </div>
      )}

      <FooterOne />
    </>
  );
}

export default VideoShowcasePage;

